import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Clock, ArrowRight, BookOpen, Calculator, CheckCircle, Circle, PlayCircle, Flame, Trophy, Target, Calendar } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PerformanceInsights from '@/components/PerformanceInsights';
import ScoreGoalWidget from '@/components/ScoreGoalWidget';

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [diagnosticTest, setDiagnosticTest] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [practiceStats, setPracticeStats] = useState(null);

  useEffect(() => {
    document.title = 'Dashboard | Practice4SAT';
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [diagnosticRes, analyticsRes, recommendationsRes, trainingPlanRes, practiceStatsRes] = await Promise.all([
        axios.get(`${API}/diagnostic-status`).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/recommendations`).catch(() => ({ data: null })),
        axios.get(`${API}/training-plan`).catch(() => ({ data: null })),
        axios.get(`${API}/practice/stats`).catch(() => ({ data: null }))
      ]);
      setDiagnosticTest(diagnosticRes.data);
      setAnalytics(analyticsRes.data);
      setRecommendations(recommendationsRes.data);
      setTrainingPlan(trainingPlanRes.data);
      setPracticeStats(practiceStatsRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startDiagnostic = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/diagnostic/start`, 
        { email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      localStorage.setItem('diagnostic_session_id', response.data.session_id);
      localStorage.setItem('diagnostic_email', user.email);
      localStorage.setItem('diagnostic_questions', JSON.stringify(response.data.questions));
      localStorage.setItem('diagnostic_section', response.data.section || 'rw');
      localStorage.setItem('diagnostic_section_name', response.data.section_name || 'Reading and Writing');
      localStorage.setItem('diagnostic_time_limit', response.data.time_limit || '1920');
      
      navigate('/diagnostic-test');
    } catch (error) {
      console.error('Error starting diagnostic test:', error);
      toast.error('Failed to start diagnostic test');
    }
  };

  // Determine user state
  // State 1: No data (no diagnostic taken)
  // State 2: Diagnostic complete but not reviewed
  // State 3: Active plan (has reviewed diagnostic or done practice/additional tests)
  const getUserState = () => {
    if (!diagnosticTest?.taken) {
      return 'no_data';
    }
    // Check if they've reviewed the diagnostic (stored in localStorage)
    const hasReviewedDiagnostic = localStorage.getItem('hasReviewedDiagnostic') === 'true';
    // Check if they've done any practice or additional tests after the diagnostic
    const hasAdditionalActivity = (analytics?.total_tests > 1) || (analytics?.total_practice_sessions > 0);
    
    if (!hasReviewedDiagnostic && !hasAdditionalActivity) {
      return 'diagnostic_complete';
    }
    return 'active_plan';
  };

  const userState = getUserState();

  // Get scores for progress snapshot
  const getScores = () => {
    if (!diagnosticTest?.taken && !analytics?.recent_attempts?.length) {
      return null;
    }
    
    const rwScore = diagnosticTest?.rw_score || analytics?.recent_attempts?.[0]?.rw_score || null;
    const mathScore = diagnosticTest?.math_score || analytics?.recent_attempts?.[0]?.math_score || null;
    const totalScore = diagnosticTest?.final_score || analytics?.recent_attempts?.[0]?.score || null;
    
    let rwChange = null;
    let mathChange = null;
    if (analytics?.recent_attempts?.length >= 2) {
      const latest = analytics.recent_attempts[0];
      const previous = analytics.recent_attempts[1];
      if (latest.rw_score && previous.rw_score) {
        rwChange = latest.rw_score - previous.rw_score;
      }
      if (latest.math_score && previous.math_score) {
        mathChange = latest.math_score - previous.math_score;
      }
    }
    
    return { rw: rwScore, math: mathScore, total: totalScore, rwChange, mathChange };
  };

  const scores = getScores();

  const handleRecommendationClick = async (rec) => {
    // Start a practice session directly for Today's Focus recommendations
    if (rec.action_type === 'practice' && rec.topic) {
      try {
        const response = await axios.post(`${API}/practice/sessions/start`, {
          topic: rec.topic,
          difficulty: 'mixed',
          mode: 'practice',
          count: 10
        });
        
        if (response.data?.session_id && response.data?.questions?.length > 0) {
          // Navigate with state for PracticeSession component
          navigate(`/practice-session/${response.data.session_id}`, {
            state: {
              sessionId: response.data.session_id,
              questions: response.data.questions,
              mode: 'practice',
              topic: rec.topic,
              returnPath: '/dashboard'
            }
          });
        } else {
          toast.error('No questions available for this topic');
          if (rec.deep_link) {
            navigate(rec.deep_link);
          }
        }
      } catch (error) {
        console.error('Error starting practice session:', error);
        toast.error('Failed to start practice session');
        // Fallback to navigation
        if (rec.deep_link) {
          navigate(rec.deep_link);
        }
      }
    } else if (rec.deep_link) {
      navigate(rec.deep_link);
    }
  };

  const handleTrainingTaskClick = async (task) => {
    // For practice tasks, start a practice session
    if (task.task_type === 'practice' && task.domain) {
      try {
        const response = await axios.post(`${API}/practice/sessions/start`, {
          topic: task.domain,
          difficulty: task.difficulty,
          mode: 'practice',
          count: 10
        });
        
        if (response.data?.session_id && response.data?.questions?.length > 0) {
          navigate(`/practice-session/${response.data.session_id}`, {
            state: {
              sessionId: response.data.session_id,
              questions: response.data.questions,
              mode: 'practice',
              topic: task.domain,
              trainingTaskId: task.task_id,
              returnPath: '/dashboard'
            }
          });
        } else {
          toast.error('No questions available for this topic');
          navigate(task.deep_link);
        }
      } catch (error) {
        console.error('Error starting training task:', error);
        navigate(task.deep_link);
      }
    } else if (task.task_type === 'full_test') {
      // For full-length test, find the next untaken test and navigate to it
      try {
        const testsResponse = await axios.get(`${API}/tests`);
        const tests = testsResponse.data || [];
        
        if (!tests.length) {
          toast.error('No tests available');
          navigate('/tests');
          return;
        }
        
        // Find first test the student hasn't taken yet
        const untakenTest = tests.find(t => t.test_id !== 'diagnostic' && !t.taken);
        const targetTest = untakenTest || tests.find(t => t.test_id !== 'diagnostic');
        
        if (targetTest) {
          navigate(`/test/${targetTest.test_id}`, {
            state: {
              trainingTaskId: task.task_id,
              returnPath: '/dashboard'
            }
          });
        } else {
          toast.error('No test available');
          navigate('/tests');
        }
      } catch (error) {
        console.error('Error finding full-length test:', error);
        toast.error('Failed to load tests');
        navigate('/tests');
      }
    } else if (task.task_type === 'review') {
      // Navigate to the analytics page for the completed test
      navigate(task.deep_link || '/analytics');
    } else if (task.task_type === 'section') {
      // For section tests - navigate to tests with section filter
      navigate(task.deep_link || '/tests');
    } else {
      // Default - navigate to deep link
      navigate(task.deep_link || '/tests');
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading dashboard...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="authenticated" 
        user={user} 
        setUser={setUser} 
        logoClickPath="/dashboard"
        activePage="dashboard"
      />

      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div className="dashboard-content">
        
        {/* Welcome Message */}
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1a202c' }} data-testid="dashboard-title">
          Welcome back, {user?.name}!
        </h1>
        <p style={{ fontSize: '1rem', color: '#718096', marginBottom: '1.5rem' }}>
          Continue your SAT preparation
        </p>

        {/* Today's Focus Headline */}
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#4c1d95' }}>
          Today's Focus
        </h2>

        {/* Hero Section */}
        <div style={{ 
          marginBottom: '2rem'
        }} className="dashboard-hero-grid">
          
          {/* LEFT SIDE - Today's Focus */}
          <div>
            {userState === 'no_data' && (
              // State 1: No Data - Show diagnostic CTA
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                borderRadius: '12px', 
                padding: '1.5rem',
                color: 'white',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                  Take the Full-Length Adaptive Diagnostic
                </h2>
                <p style={{ opacity: 0.9, marginBottom: '1rem', lineHeight: '1.5', flex: 1 }}>
                  See your estimated score and unlock personalized recommendations.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: 'auto' }}>
                  <Button 
                    onClick={startDiagnostic}
                    style={{ 
                      background: 'white',
                      color: '#667eea',
                      padding: '0.75rem 1.5rem',
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}
                    data-testid="start-diagnostic-btn"
                  >
                    Start Diagnostic
                    <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
                  </Button>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', opacity: 0.9, fontSize: '0.9rem' }}>
                    <Clock size={16} />
                    ~2 hours
                  </span>
                </div>
              </div>
            )}

            {userState === 'diagnostic_complete' && (
              // State 2: Diagnostic Complete - Show review CTA
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                borderRadius: '12px', 
                padding: '1.5rem',
                color: 'white',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                  Review Your Diagnostic Results
                </h2>
                <p style={{ opacity: 0.9, marginBottom: '1rem', lineHeight: '1.5', flex: 1 }}>
                  See your score breakdown and priority skills before starting your plan.
                </p>
                <Button 
                  onClick={() => {
                    localStorage.setItem('hasReviewedDiagnostic', 'true');
                    navigate(`/analytics/${diagnosticTest.session_id}`);
                  }}
                  style={{ 
                    background: 'white',
                    color: '#667eea',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    marginTop: 'auto',
                    width: 'fit-content'
                  }}
                  data-testid="review-results-btn"
                >
                  Review Results
                  <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
                </Button>
              </div>
            )}

            {userState === 'active_plan' && recommendations && (
              // State 3: Active Plan - Show two recommendation cards side by side
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1rem',
                height: '100%'
              }} className="recommendations-grid">
                  
                  {/* Reading & Writing Card */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', 
                    borderRadius: '12px', 
                    padding: '1.25rem',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <BookOpen size={20} />
                      <span style={{ fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.9 }}>
                        Reading & Writing
                      </span>
                    </div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {recommendations.rw_recommendation?.title || 'R&W Practice'}
                    </h3>
                    <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '1rem', lineHeight: '1.4', flex: 1 }}>
                      {recommendations.rw_recommendation?.reason || 'Focus on key reading and writing skills.'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                      <Button 
                        onClick={() => handleRecommendationClick(recommendations.rw_recommendation)}
                        style={{ 
                          background: 'white',
                          color: '#6366f1',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        {recommendations.rw_recommendation?.cta_label || 'Start RW'}
                        <ArrowRight size={16} style={{ marginLeft: '0.35rem' }} />
                      </Button>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.9, fontSize: '0.8rem' }}>
                        <Clock size={14} />
                        ~{recommendations.rw_recommendation?.estimated_time_minutes || 15} min
                      </span>
                    </div>
                  </div>

                  {/* Math Card */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', 
                    borderRadius: '12px', 
                    padding: '1.25rem',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Calculator size={20} />
                      <span style={{ fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.9 }}>
                        Math
                      </span>
                    </div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {recommendations.math_recommendation?.title || 'Math Practice'}
                    </h3>
                    <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '1rem', lineHeight: '1.4', flex: 1 }}>
                      {recommendations.math_recommendation?.reason || 'Build your math problem-solving skills.'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                      <Button 
                        onClick={() => handleRecommendationClick(recommendations.math_recommendation)}
                        style={{ 
                          background: 'white',
                          color: '#0284c7',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        {recommendations.math_recommendation?.cta_label || 'Start Math'}
                        <ArrowRight size={16} style={{ marginLeft: '0.35rem' }} />
                      </Button>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.9, fontSize: '0.8rem' }}>
                        <Clock size={14} />
                        ~{recommendations.math_recommendation?.estimated_time_minutes || 15} min
                      </span>
                    </div>
                  </div>
                </div>
            )}
          </div>

          {/* RIGHT SIDE - Streak & Activity */}
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {/* Streak Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ 
                width: '52px', height: '52px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Flame size={28} color="white" />
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1a202c', lineHeight: 1 }} data-testid="streak-current">
                  {practiceStats?.streak?.current || 0}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                  day streak
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ background: '#f7fafc', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                  <Trophy size={14} color="#f59e0b" />
                  <span style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Best Streak</span>
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a202c' }} data-testid="streak-longest">
                  {practiceStats?.streak?.longest || 0} days
                </div>
              </div>
              <div style={{ background: '#f7fafc', borderRadius: '8px', padding: '0.65rem 0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                  <Target size={14} color="#10b981" />
                  <span style={{ fontSize: '0.7rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Accuracy</span>
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1a202c' }} data-testid="streak-accuracy">
                  {practiceStats?.overall_accuracy || 0}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Goal & Mistake Journal */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0rem' }}>
          <ScoreGoalWidget />
          <div
            data-testid="mistake-journal-card"
            onClick={() => navigate('/mistake-journal')}
            style={{
              background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e5e7eb',
              cursor: 'pointer', transition: 'box-shadow 0.2s',
              display: 'flex', flexDirection: 'column', justifyContent: 'center'
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <BookOpen size={20} style={{ color: '#ef4444' }} />
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1a202c', margin: 0 }}>Mistake Journal</h3>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 0.75rem 0', lineHeight: 1.5 }}>
              Review your wrong answers, tag why you got them wrong, and track your improvement.
            </p>
            <span style={{ color: '#6366f1', fontWeight: 600, fontSize: '0.9rem' }}>
              Open Journal &rarr;
            </span>
          </div>
        </div>

        {/* 4-Week Study Plan Section */}
        <TrainingPlanSection 
          trainingPlan={trainingPlan} 
          navigate={navigate}
          onTaskClick={handleTrainingTaskClick}
        />

        {/* Overall Performance Overview */}
        <PerformanceInsights />

        </div>
      </div>

      <Footer />
    </div>
  );
}

// Training Plan Task Click Handler (moved outside component for clarity)
function TrainingPlanSection({ trainingPlan, navigate, onTaskClick }) {
  if (!trainingPlan) return null;
  
  if (!trainingPlan.has_plan) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1a202c' }}>
          Your 4-Week Study Plan
        </h2>
        <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1rem' }}>
          Built from your performance data. Updates after milestone tests.
        </p>
        <div style={{ 
          background: '#f8fafc', 
          borderRadius: '12px', 
          padding: '2rem',
          textAlign: 'center',
          border: '1px solid #e2e8f0'
        }}>
          <p style={{ color: '#64748b', fontSize: '1rem' }}>
            {trainingPlan.message || 'Complete the diagnostic to generate your 4-week plan.'}
          </p>
        </div>
      </div>
    );
  }
  
  const plan = trainingPlan.plan;
  
  const getStatusIcon = (status) => {
    if (status === 'complete') return <CheckCircle size={18} style={{ color: '#10b981' }} />;
    if (status === 'in_progress') return <PlayCircle size={18} style={{ color: '#3b82f6' }} />;
    return <Circle size={18} style={{ color: '#cbd5e1' }} />;
  };
  
  const TaskRow = ({ task, disabled = false, disabledReason = null }) => {
    const isComplete = task.status === 'complete';
    const isDisabled = disabled || isComplete;
    
    return (
      <div 
        onClick={() => !isDisabled && onTaskClick(task)}
        title={disabledReason || ''}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          background: isComplete ? '#f8fafc' : disabled ? '#f1f5f9' : 'white',
          borderRadius: '8px',
          marginBottom: '0.5rem',
          cursor: isDisabled ? 'default' : 'pointer',
          border: '1px solid #e2e8f0',
          opacity: isDisabled ? 0.6 : 1,
          transition: 'all 0.2s ease'
        }}
        className={!isDisabled ? 'hover:shadow-sm' : ''}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {getStatusIcon(task.status)}
          <div>
            <span style={{ 
              fontWeight: '600', 
              color: disabled ? '#94a3b8' : '#1a202c', 
              fontSize: '0.9rem',
              textDecoration: isComplete ? 'line-through' : 'none'
            }}>
              {task.domain}
            </span>
            {disabledReason && (
              <span style={{ 
                display: 'block',
                fontSize: '0.75rem', 
                color: '#94a3b8',
                marginTop: '0.2rem'
              }}>
                {disabledReason}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            ~{task.estimated_minutes} min
          </span>
        </div>
      </div>
    );
  };
  
  const WeekCard = ({ week }) => {
    const progressPercent = week.total_count > 0 ? (week.completed_count / week.total_count) * 100 : 0;
    const isWeek4 = week.week_number === 4;
    
    // Filter out shared tasks (full_test and review) for Week 4
    const rwDomainTasks = isWeek4 
      ? week.rw_tasks.filter(t => t.task_type === 'practice')
      : week.rw_tasks;
    const mathDomainTasks = isWeek4
      ? week.math_tasks.filter(t => t.task_type === 'practice')
      : week.math_tasks;
    const sharedTasks = isWeek4
      ? week.rw_tasks.filter(t => t.task_type === 'full_test' || t.task_type === 'review')
      : [];
    
    return (
      <div style={{ 
        background: '#fafafa', 
        borderRadius: '12px', 
        padding: '1.25rem',
        marginBottom: '1rem',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
      }}>
        {/* Week Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1a202c', margin: 0 }}>
              Week {week.week_number} — {week.title}
            </h4>
          </div>
          <span style={{ 
            fontSize: '0.8rem', 
            color: '#64748b',
            background: '#f1f5f9',
            padding: '0.25rem 0.75rem',
            borderRadius: '12px'
          }}>
            {week.progress_text}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{ 
          height: '4px', 
          background: '#e2e8f0', 
          borderRadius: '2px', 
          marginBottom: '1rem',
          overflow: 'hidden'
        }}>
          <div style={{ 
            height: '100%', 
            width: `${progressPercent}%`, 
            background: progressPercent === 100 ? '#10b981' : '#3b82f6',
            borderRadius: '2px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        
        {/* Two-Column Subject Cards */}
        <div className="training-week-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem'
        }}>
          {/* RW Tasks - Subtle purple gradient */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(167, 139, 250, 0.05) 100%)', 
            borderRadius: '8px', 
            padding: '1rem',
            border: '1px solid rgba(139, 92, 246, 0.15)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '0.75rem'
            }}>
              <BookOpen size={16} style={{ color: '#8b5cf6' }} />
              <span style={{ fontWeight: '600', color: '#7c3aed', fontSize: '0.85rem' }}>Reading & Writing</span>
            </div>
            {rwDomainTasks.map(task => (
              <TaskRow key={task.task_id} task={task} />
            ))}
          </div>
          
          {/* Math Tasks - Subtle cyan gradient */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.08) 0%, rgba(34, 211, 238, 0.05) 100%)', 
            borderRadius: '8px', 
            padding: '1rem',
            border: '1px solid rgba(8, 145, 178, 0.15)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '0.75rem'
            }}>
              <Calculator size={16} style={{ color: '#0891b2' }} />
              <span style={{ fontWeight: '600', color: '#0e7490', fontSize: '0.85rem' }}>Math</span>
            </div>
            {mathDomainTasks.map(task => (
              <TaskRow key={task.task_id} task={task} />
            ))}
          </div>
        </div>
        
        {/* Week 4: Full-Length Test & Review - Side by Side with different color */}
        {isWeek4 && sharedTasks.length > 0 && (
          <div style={{ 
            marginTop: '1rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.05) 100%)',
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.15)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '0.75rem'
            }}>
              <span style={{ fontWeight: '600', color: '#b45309', fontSize: '0.85rem' }}>Final Assessment</span>
            </div>
            {/* Side by side layout for test and review */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '0.75rem' 
            }}>
              {sharedTasks.map(task => {
                const fullTestTask = sharedTasks.find(t => t.task_type === 'full_test');
                const isReviewDisabled = task.task_type === 'review' && fullTestTask?.status !== 'complete';
                
                return (
                  <TaskRow 
                    key={task.task_id} 
                    task={task} 
                    disabled={isReviewDisabled}
                    disabledReason={isReviewDisabled ? 'Complete the Full-Length Test first' : null}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div style={{ marginTop: '2rem' }} data-testid="training-plan-section">
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1a202c' }}>
        Your 4-Week Study Plan
      </h2>
      <p style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1.5rem' }}>
        Built from your performance data. Updates after milestone tests.
      </p>
      
      {plan.weeks.map(week => (
        <WeekCard key={week.week_number} week={week} />
      ))}
      
      {plan.cycle_number > 1 && (
        <p style={{ 
          fontSize: '0.85rem', 
          color: '#64748b', 
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Cycle {plan.cycle_number}
        </p>
      )}
    </div>
  );
}
